import React, { useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import { Client } from '../models';

interface ProcessSelectorProps {
  client: Client;
  onProcessChange: (clientId: string, newProcess: string) => Promise<void>;
  onProcessUpdated?: () => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  showNotifications?: boolean;
}

const procesoOptions = [
  'Ingresado en Xpress Pago',
  'Link de pago enviado',
  'Inscripción pagada',
  'Mensualidad programada',
  'Tarjeta inválida',
  'Fondos insuficientes',
  'Link de pago no completado',
  'Otro'
];

// Estados por color para los indicadores
const procesoGreen = ['Inscripción pagada', 'Mensualidad programada', 'Ingresado en Xpress Pago', 'Link de pago enviado'];
const procesoRed = ['Tarjeta inválida', 'Fondos insuficientes', 'Link de pago no completado'];
const procesoSuccess = ['Inscripción pagada', 'Mensualidad programada'];

// Función para renderizar opciones del select con indicadores de color
const renderProcesoOption = (option: string) => {
  let color = '#1976d2'; // Azul por defecto
  let icon = null;
  
  if (procesoGreen.includes(option)) {
    color = '#2e7d32'; // Verde
    icon = '✓';
  } else if (procesoRed.includes(option)) {
    color = '#d32f2f'; // Rojo
    icon = '✗';
  }
  
  return (
    <MenuItem key={option} value={option}>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {icon && (
          <Box 
            sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              backgroundColor: color, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: 'white',
              fontSize: '10px',
              fontWeight: 'bold',
              mr: 1
            }}
          >
            {icon}
          </Box>
        )}
        <Typography sx={{ color: color, fontWeight: icon ? 'bold' : 'normal' }}>
          {option}
        </Typography>
      </Box>
    </MenuItem>
  );
};

const ProcessSelector: React.FC<ProcessSelectorProps> = ({
  client,
  onProcessChange,
  onProcessUpdated,
  disabled = false,
  size = 'small',
  showNotifications = true
}) => {
  const [localProceso, setLocalProceso] = useState(client.proceso || '');
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' as 'success' | 'error' });

  const handleProcessChange = async (event: any) => {
    const newProcess = event.target.value;
    console.log('ProcessSelector: Cambiando proceso para cliente', client.id, 'a:', newProcess);
    setLocalProceso(newProcess);

    if (showNotifications) {
      setNotification({ open: true, message: 'Actualizando proceso...', type: 'success' });
    }
    
    try {
      console.log('ProcessSelector: Iniciando actualización en base de datos...');
      await onProcessChange(client.id || '', newProcess);
      console.log('ProcessSelector: Actualización en base de datos completada exitosamente');
      
      if (showNotifications) {
        setNotification({ open: true, message: 'Proceso actualizado exitosamente', type: 'success' });
      }
      
      if (onProcessUpdated) {
        console.log('ProcessSelector: Iniciando recarga de datos...');
        await onProcessUpdated();
        console.log('ProcessSelector: Recarga de datos completada');
      }
    } catch (error) {
      console.error('ProcessSelector: Error al actualizar proceso:', error);
      if (showNotifications) {
        setNotification({ open: true, message: 'Error al actualizar proceso', type: 'error' });
      }
      setLocalProceso(client.proceso || '');
    }
  };

  // Sincronizar el estado local cuando cambia el prop client.proceso
  React.useEffect(() => {
    setLocalProceso(client.proceso || '');
  }, [client.proceso]);

  const isSuccess = procesoSuccess.includes(localProceso);

  return (
    <Box sx={{ minWidth: 200 }}>
      <FormControl fullWidth size={size} disabled={disabled}>
        <InputLabel id={`process-select-label-${client.id}`}>
          Proceso
        </InputLabel>
        <Select
          labelId={`process-select-label-${client.id}`}
          value={localProceso}
          label="Proceso"
          onChange={handleProcessChange}
        >
          {procesoOptions.map(renderProcesoOption)}
        </Select>
      </FormControl>
      {isSuccess && (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
          Proceso completado
        </Typography>
      )}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.type}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcessSelector;
